import { Extension, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as PMNode } from "@tiptap/pm/model";
import { PLACEHOLDER_RE, cleanPlaceholder } from "./emailBody";

/** Highlight {{placeholders}} without changing the document. */
export function placeholderDecorations(doc: PMNode): DecorationSet {
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    // One character per position: text is 1:1 and each inline leaf (hard break) maps to one char.
    const text = node.textBetween(0, node.content.size, undefined, "￼");
    for (const match of text.matchAll(PLACEHOLDER_RE)) {
      const from = pos + 1 + (match.index ?? 0);
      const label = cleanPlaceholder(match[0]);
      decorations.push(
        Decoration.inline(from, from + match[0].length, {
          class: "email-placeholder",
          "data-placeholder": label,
          title: `Add your ${label} in your profile`,
        })
      );
    }
    return false;
  });
  return DecorationSet.create(doc, decorations);
}

const placeholderKey = new PluginKey<DecorationSet>("emailPlaceholders");

const PlaceholderHighlight = Extension.create({
  name: "emailPlaceholderHighlight",
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: placeholderKey,
        state: {
          init: (_, state) => placeholderDecorations(state.doc),
          apply: (tr, old) => (tr.docChanged ? placeholderDecorations(tr.doc) : old),
        },
        props: {
          decorations: state => placeholderKey.getState(state),
        },
      }),
    ];
  },
});

export const emailEditorExtensions = [
  StarterKit.configure({
    heading: false,
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    link: { openOnClick: false, autolink: true, defaultProtocol: "https" },
  }),
  TextStyle,
  Color,
  PlaceholderHighlight,
];

/** Text the way the API derives it: blank line between paragraphs, newline for line breaks. */
export function editorPlainText(editor: Editor): string {
  return editor.getText({ blockSeparator: "\n\n" });
}
